import React, { useEffect, useState } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import api from "../../lib/api";

const NAV = [
    { to: "/", label: "Accueil", end: true },
    { to: "/actualites", label: "Actualités" },
    { to: "/contact", label: "Contact" },
    { to: "/inscription/assmbb", label: "Inscription" },
];

export default function PublicLayout() {
    const [open, setOpen] = useState(false);
    const [club, setClub] = useState(null);

    useEffect(() => {
        setOpen(false);
        api.get("/public/clubs/by-name/ASSMBB")
            .then(({ data }) => api.get(`/public/clubs/${data.id}/homepage`))
            .then(({ data }) => setClub(data.club))
            .catch(() => {});
    }, []);

    const clubName = club?.name || "Club";

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
                        {club?.logo_url && <img src={club.logo_url} alt={clubName} className="w-16 h-16 object-contain" />}
                        <div>
                            <div className="font-display font-bold leading-tight">{clubName}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                {club?.sport_type || "Club sportif"}
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "text-orange-500" : "text-slate-700 hover:text-orange-500"
                                    }`
                                }
                                data-testid={`public-nav-${n.to.replace("/", "") || "home"}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}

                        <Link
                            to="/login"
                            className="ml-2 inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
                            data-testid="public-login-btn"
                        >
                            Espace club
                        </Link>
                    </nav>

                    <button
                        className="md:hidden text-slate-700"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden border-t border-slate-200 bg-white px-6 py-3 space-y-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `block px-3 py-2 rounded-md text-sm ${
                                        isActive ? "bg-orange-50 text-orange-600 font-semibold" : "text-slate-700"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}

                        <Link
                            to="/login"
                            className="block bg-orange-500 text-white text-center font-semibold rounded-lg px-4 py-2 mt-2"
                        >
                            Espace club
                        </Link>
                    </div>
                )}
            </header>

            <main>
                <Outlet />
            </main>

            <footer className="bg-slate-900 text-slate-300 mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            {club?.logo_url && <img src={club.logo_url} alt={clubName} className="w-12 h-12 object-contain" />}
                            <div>
                                <div className="font-display font-bold text-white leading-tight">
                                    {clubName}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                    {club?.sport_type || "Club sportif"}
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-slate-400">
                            {club?.description || `Site officiel de ${clubName}.`}
                        </p>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
                            Navigation
                        </div>
                        <ul className="space-y-1 text-sm">
                            {NAV.map((n) => (
                                <li key={n.to}>
                                    <Link to={n.to} className="hover:text-orange-400">
                                        {n.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
                            Club
                        </div>
                        <ul className="space-y-1 text-sm">
                            <li>{clubName}</li>
                            {club?.address && <li>{club.address}</li>}
                            {club?.email && <li><a href={`mailto:${club.email}`} className="hover:text-orange-400">{club.email}</a></li>}
                            {club?.phone && <li>{club.phone}</li>}
                            {club?.website && <li><a href={club.website} className="hover:text-orange-400" rel="noreferrer" target="_blank">Site web</a></li>}
                        </ul>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
                            Espace club
                        </div>
                        <p className="text-sm text-slate-400 mb-3">
                            Accès réservé aux membres autorisés du club.
                        </p>
                        <Link to="/login" className="text-sm font-semibold text-orange-400 hover:text-orange-300">
                            Se connecter →
                        </Link>
                    </div>
                </div>

                <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
                    © 2026 Mada Smart Services • Tous droits réservés
                </div>
            </footer>
        </div>
    );
}
